import { useEffect } from 'react';
import { useToastStore } from '@/stores/toastStore';
import { cn } from '@/lib/utils';
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const toneStyles = {
  success: 'border-success-100 bg-success-50 text-success-700',
  error: 'border-danger-100 bg-danger-50 text-danger-700',
  info: 'border-info-100 bg-info-50 text-info-700',
  warning: 'border-warning-100 bg-warning-50 text-warning-700',
};

const toneIcons = {
  success: <CheckCircle2 className="h-4 w-4" />,
  error: <AlertCircle className="h-4 w-4" />,
  info: <Info className="h-4 w-4" />,
  warning: <AlertTriangle className="h-4 w-4" />,
};

const DEFAULT_DURATION_MS = 5000;

export const ToastViewport = () => {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  // Defense-in-depth auto-dismiss: the store normally schedules a dismiss
  // timeout when a toast is pushed, but if a toast ever arrives without
  // a duration (or with duration <= 0 we keep it pinned), make sure it
  // disappears so toasts never pile up forever.
  useEffect(() => {
    const timers = toasts
      .filter((t) => !t.duration || t.duration <= 0)
      .map((t) => window.setTimeout(() => dismiss(t.id), DEFAULT_DURATION_MS));
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [toasts, dismiss]);

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2"
      role="status"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          data-toast
          className={cn(
            'pointer-events-auto flex items-start gap-3 rounded-md border bg-surface-base p-3 shadow-lg animate-slide-up',
            toneStyles[t.tone]
          )}
          role="alert"
        >
          <div className="flex-shrink-0 pt-0.5">{toneIcons[t.tone]}</div>
          <div className="flex-1 space-y-0.5">
            <p className="text-sm font-semibold text-fg-base">{t.title}</p>
            {t.description && <p className="text-xs text-fg-subtle">{t.description}</p>}
          </div>
          {t.action && (
            <button
              type="button"
              onClick={() => {
                t.action?.onClick();
                dismiss(t.id);
              }}
              className="shrink-0 rounded px-2 py-1 text-xs font-semibold text-burgundy-700 transition-colors hover:bg-burgundy-50"
            >
              {t.action.label}
            </button>
          )}
          <button
            type="button"
            aria-label="Fermer la notification"
            onClick={() => dismiss(t.id)}
            className="rounded p-1 text-fg-muted transition-colors hover:bg-surface-muted hover:text-fg-base"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      ))}
    </div>
  );
};
